import { useEffect, useRef, useState } from 'react'
import { useMeetingsStore } from '../store/meetingsStore'
import {
  requestNotificationPermission,
  sendDesktopNotification,
  sendEmailNotification,
  formatMeetingForNotification,
  getNotificationPreferences,
  saveNotificationPreferences,
  playNotificationSound,
  shouldNotifyAttendee,
  getTimeUntilMeeting,
} from '../utils/notificationsUtils'

/**
 * Custom hook for managing meeting notifications
 * Handles desktop notifications and email reminders
 */
export const useNotifications = (userEmail = null) => {
  const meetings = useMeetingsStore((state) => state.meetings)
  const [notificationPermission, setNotificationPermission] = useState(false)
  const [preferences, setPreferences] = useState(null)
  const notificationIntervalRef = useRef(null)
  const sentNotificationsRef = useRef(new Set())

  // Initialize notifications on mount
  useEffect(() => {
    const initializeNotifications = async () => {
      // Load saved preferences
      const savedPreferences = getNotificationPreferences()
      setPreferences(savedPreferences)

      // Request permission if not already granted
      if (savedPreferences.desktopNotifications) {
        const permission = await requestNotificationPermission()
        setNotificationPermission(permission)
      }
    }

    initializeNotifications()
  }, [])

  // Check for upcoming meetings and send reminders
  useEffect(() => {
    if (!preferences) return

    const checkForReminders = () => {
      const now = new Date()

      meetings.forEach((meeting) => {
        const timeUntil = getTimeUntilMeeting(meeting.startTime)

        if (!timeUntil) {
          // Meeting already started or in the past
          return
        }

        // Check each reminder minute threshold
        preferences.reminderMinutes.forEach((reminderMin) => {
          const notificationKey = `${meeting.id}-${reminderMin}min`

          // Check if we already sent this notification
          if (sentNotificationsRef.current.has(notificationKey)) {
            return
          }

          // Check if it's time to send this reminder
          if (
            timeUntil.minutes <= reminderMin &&
            timeUntil.minutes > reminderMin - 1
          ) {
            // Send notifications to attendees based on their preferences
            meeting.attendees.forEach((attendee) => {
              // Send desktop notification
              if (
                preferences.desktopNotifications &&
                shouldNotifyAttendee(attendee, 'desktop')
              ) {
                const notificationInfo = formatMeetingForNotification(meeting)
                sendDesktopNotification(
                  notificationInfo.title,
                  {
                    body: notificationInfo.body,
                    tag: notificationInfo.tag,
                  }
                )

                // Play sound if enabled
                if (preferences.soundEnabled) {
                  playNotificationSound()
                }
              }

              // Send email notification
              if (
                preferences.emailNotifications &&
                shouldNotifyAttendee(attendee, 'email')
              ) {
                sendEmailNotification(attendee.email, meeting, `reminder-${reminderMin}min`)
              }
            })

            // Mark this notification as sent
            sentNotificationsRef.current.add(notificationKey)
          }
        })
      })
    }

    // Check for reminders every 30 seconds
    notificationIntervalRef.current = setInterval(checkForReminders, 30000)

    // Initial check
    checkForReminders()

    return () => {
      if (notificationIntervalRef.current) {
        clearInterval(notificationIntervalRef.current)
      }
    }
  }, [meetings, preferences])

  // Function to update notification preferences
  const updatePreferences = (newPreferences) => {
    const updated = { ...preferences, ...newPreferences }
    setPreferences(updated)
    saveNotificationPreferences(updated)

    // Reset sent notifications to allow re-triggering if changed
    sentNotificationsRef.current.clear()
  }

  // Function to manually trigger a notification for testing
  const testNotification = (meeting) => {
    const notificationInfo = formatMeetingForNotification(meeting)
    sendDesktopNotification(notificationInfo.title, {
      body: notificationInfo.body,
      tag: 'test-notification',
    })

    if (preferences?.soundEnabled) {
      playNotificationSound()
    }
  }

  return {
    notificationPermission,
    preferences,
    updatePreferences,
    testNotification,
  }
}

export default useNotifications
